import { Paper, Typography, Box, Stack, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../ProgramForm/helpers/quotationCalculations";
import { Person, ChildCare, CheckCircle } from "@mui/icons-material";
import useConfirmProgram from "../../../hooks/programs/useConfirmProgram";
import { usePermissions } from "../../../auth/PermissionsContext";
import toast from "react-hot-toast";

const ProgramOverview = ({ program }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    const { has } = usePermissions();

    const { confirmProgramMutate, isPending: isConfirming } = useConfirmProgram();

    const perms = {
        confirm: has("programs_confirm"),
    };

    const handleConfirm = () => {
        if (!perms.confirm) {
            toast.error(lang === 'ar' ? "ليس لديك صلاحية التأكيد" : "No confirm permission");
            return;
        }
        confirmProgramMutate(program.id);
    };

    // Build pax types breakdown reliably from response
    const paxTypes = program?.pax_types || [];
    // const totalFromPaxTypes = paxTypes.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
    // const totalPax = (typeof program?.number_of_pax === 'number') ? program.number_of_pax : (totalFromPaxTypes || 0);

    const getPaxIcon = (nameObj) => {
        const en = (nameObj && nameObj.en) ? String(nameObj.en).toLowerCase() : '';
        const ar = (nameObj && nameObj.ar) ? String(nameObj.ar) : '';
        if (en.includes('adult') || ar.includes('بالغ')) return <Person fontSize="small" sx={{ color: '#424242', fontSize: '18px' }} />;
        if (en.includes('child') || ar.includes('أطفال')) return <Person fontSize="small" sx={{ color: '#424242', fontSize: '14px' }} />;
        if (en.includes('infant') || ar.includes('رضيع')) return <ChildCare fontSize="small" sx={{ color: '#424242', fontSize: '18px' }} />;
        return <Person fontSize="small" sx={{ color: '#424242', fontSize: '18px' }} />;
    };

    return (
        <Paper
            className="overview-section"
            sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '8px',
                    backgroundColor: '#FFD700',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    border: '2px solid #FFD700',
                },
                '@media print': {
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    marginBottom: '16px',
                    padding: '12px',
                    boxShadow: 'none',
                    border: '1px solid #ddd',
                }
            }}
        >
            <Box className="overview-content" sx={{ pl: 3 }}>

                {/* Top Row: use grid so icon widths don't push text out of alignment */}
                <Box className="overview-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',mb: 1, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 0.5, fontSize: '16px' }}>
                            {lang === 'ar' ? "اسم الوكيل" : "Agent Name"}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#424242', fontSize: '18px' }}>
                            {program?.agent_name?.[lang] || program?.agent_name || ''}
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 0.5, fontSize: '16px' }}>
                            {lang === 'ar' ? "رقم التأكيد" : "Confirmation Number"}
                        </Typography>
                        {program?.confirmed ? (
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#424242', fontSize: '18px' }}>
                                {program?.confirmation_number || `CNF-${program?.id || '111'}`}
                            </Typography>
                        ) : (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircle />}
                                onClick={handleConfirm}
                                disabled={isConfirming}
                                color="success"
                                sx={{ minWidth: 120 }}
                            >
                                {isConfirming ? (lang === 'ar' ? "جاري..." : "Confirming...") : (lang === 'ar' ? "تأكيد" : "Confirm")}
                            </Button>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pl: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 0.5, fontSize: '15px' }}>
                            {lang === 'ar' ? "رقم الطلب" : "Request Number"}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#424242', fontSize: '17px' }}>
                            {program?.request_number || `RQ-${program?.id ? String(program.id).padStart(4, '0') : '0111'}`}
                        </Typography>
                    </Box>
                </Box>

                {/* Middle Row */}
                <Box className="overview-row-client" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 0.5, fontSize: '15px' }}>
                        {lang === 'ar' ? "اسم العميل" : "Client Name"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#424242', fontSize: '24px', mb: 0.5 }}>
                        {program?.description?.[lang] || ''}
                    </Typography>
                </Box>

                {/* Bottom Row */}
                <Box className="overview-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 0.75, fontSize: '15px' }}>
                            {lang === 'ar' ? "الحضور" : "Attendees"}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            {paxTypes.length > 0 ? (
                                paxTypes.map((pt) => (
                                    <Stack key={pt.id || pt.pax_type_id} direction="row" spacing={0.5} alignItems="center">
                                        {getPaxIcon(pt.name)}
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242', fontSize: '15px' }}>
                                            {pt.quantity ?? 0} {lang === 'ar' ? pt.name?.ar || pt.name?.en : pt.name?.en || pt.name?.ar}
                                        </Typography>
                                    </Stack>
                                ))
                            ) : (
                                <>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Person fontSize="small" sx={{ color: '#424242', fontSize: '18px' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242', fontSize: '15px' }}>
                                            {program?.number_of_pax || 0} {lang === 'ar' ? 'بالغون' : 'Adults'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Person fontSize="small" sx={{ color: '#424242', fontSize: '14px' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242', fontSize: '15px' }}>
                                            {program?.child_count ?? 0} {lang === 'ar' ? 'أطفال' : 'Child'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <ChildCare fontSize="small" sx={{ color: '#424242', fontSize: '18px' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242', fontSize: '15px' }}>
                                            {program?.infant_count ?? 0} {lang === 'ar' ? 'رضع' : 'Infant'}
                                        </Typography>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                        {(() => {
                            const displayPrice = program?.display_price || program?.price || 14100;
                            const currency = program?.display_currency || program?.currency;
                            const symbol = currency?.symbol || '฿';
                            const currencyName = currency?.name?.[lang] || currency?.name || 'THB';
                            
                            return (
                                <>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#424242', fontSize: '30px' }}>
                                        {formatCurrency(displayPrice)} <span style={{ fontSize: '24px', fontWeight: 600 }}>{symbol}</span>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 500 }}>
                                        {currencyName}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px', fontWeight: 500 }}>
                                        {lang === 'ar' ? "(شامل جميع الضرائب والخدمات)" : "(Inclusive of all taxes & services)"}
                                    </Typography>
                                </>
                            );
                        })()}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export default ProgramOverview;